"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  type BriefGroup,
  groupBriefsByCategory,
} from "../../_lib/brief-grouping";
import type { ExploreBriefCard } from "../../types";
import type { BriefOpenMethod } from "../use-brief-analytics";
import {
  clearBriefDeepLinkParams,
  useBriefDeepLink,
} from "../use-brief-deep-link";
import { BriefPreviewDialog } from "./brief-preview-dialog";
import { BriefStoriesVerticalOverlay } from "./briefs-stories-vertical-overlay";
import { PlayAllButton } from "./play-all-button";

// ─── Category dot colors (for mobile compact rows) ───────────

const CATEGORY_DOT_COLORS: Record<string, string> = {
  "video-and-film": "var(--explore-accent-red)",
  "image-and-design": "var(--explore-accent-purple)",
  "audio-and-voice": "var(--explore-accent-orange)",
  "product-design": "var(--explore-accent-teal)",
  "vibe-coding": "var(--explore-accent-deal)",
  writing: "var(--explore-text-muted)",
  other: "var(--explore-text-muted)",
};

function getCategoryDotColor({ slug }: { slug: string }): string {
  return CATEGORY_DOT_COLORS[slug] ?? "var(--explore-text-muted)";
}

const MOBILE_MAX_VISIBLE = 3;

// ─── Mobile compact brief row (colored dot + title + time) ───

function MobileCompactBriefRow({
  brief,
  categorySlug,
  onClick,
}: {
  brief: ExploreBriefCard;
  categorySlug: string;
  onClick: () => void;
}) {
  return (
    <button
      className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[var(--explore-bg-highlight)]"
      onClick={onClick}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <span
        className="h-2 w-2 shrink-0 rounded-full"
        style={{ backgroundColor: getCategoryDotColor({ slug: categorySlug }) }}
      />
      <p className="line-clamp-2 min-w-0 flex-1 font-medium text-[0.82rem] text-[var(--explore-text-primary)] leading-[1.4]">
        {brief.title}
      </p>
      <span className="shrink-0 text-[0.65rem] text-[var(--explore-text-muted)] tabular-nums">
        {brief.timeAgo}
      </span>
    </button>
  );
}

// ─── Mobile category group with controlled accordion ─────────

function MobileCategoryGroup({
  group,
  isExpanded,
  onExpand,
  onBriefClick,
}: {
  group: BriefGroup;
  isExpanded: boolean;
  onExpand: () => void;
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const hasMore = group.briefs.length > MOBILE_MAX_VISIBLE;
  const remainingCount = group.briefs.length - MOBILE_MAX_VISIBLE;
  const visibleBriefs = isExpanded
    ? group.briefs
    : group.briefs.slice(0, MOBILE_MAX_VISIBLE);

  return (
    <div>
      <div
        className="mb-1.5 flex items-center gap-2 px-4"
        style={{ fontFamily: "var(--homepage-sans-stack)" }}
      >
        <span className="text-[0.78rem]">{group.categoryEmoji}</span>
        <span className="font-semibold text-[0.78rem] text-[var(--explore-text-primary)]">
          {group.categoryDisplayName}
        </span>
        <span className="text-[0.7rem] text-[var(--explore-text-muted)]">
          ({group.briefs.length})
        </span>
      </div>

      <div className="rounded-xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)]">
        <div className="divide-y divide-[var(--explore-border-light)]">
          {visibleBriefs.map((brief) => (
            <MobileCompactBriefRow
              brief={brief}
              categorySlug={group.categorySlug}
              key={brief.briefId}
              onClick={() => onBriefClick(brief)}
            />
          ))}
        </div>
        {hasMore && !isExpanded && (
          <button
            className="w-full py-2 text-center text-[0.72rem] text-[var(--explore-text-muted)] transition-colors hover:text-[var(--explore-text-primary)]"
            onClick={onExpand}
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
            type="button"
          >
            + {remainingCount} more briefs
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Mobile briefs list with "Collapse all" ──────────────────

function MobileBriefsList({
  groups,
  onBriefClick,
}: {
  groups: BriefGroup[];
  onBriefClick: (brief: ExploreBriefCard) => void;
}) {
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(new Set());

  const handleExpand = useCallback((slug: string) => {
    setExpandedSlugs((prev) => {
      const next = new Set(prev);
      next.add(slug);
      return next;
    });
  }, []);

  const handleCollapseAll = useCallback(() => {
    setExpandedSlugs(new Set());
  }, []);

  const expandedCount = expandedSlugs.size;

  return (
    <div className="flex flex-col gap-4 px-1 lg:hidden">
      {groups.map((group) => (
        <MobileCategoryGroup
          group={group}
          isExpanded={expandedSlugs.has(group.categorySlug)}
          key={group.categorySlug}
          onBriefClick={onBriefClick}
          onExpand={() => handleExpand(group.categorySlug)}
        />
      ))}

      {/* "Collapse all" — appears when 2+ categories are expanded */}
      {expandedCount >= 2 && (
        <button
          className="mx-auto rounded-full border border-[var(--explore-border)] bg-[var(--explore-bg)] px-4 py-2 text-[0.72rem] text-[var(--explore-text-muted)] transition-colors hover:text-[var(--explore-text-primary)]"
          onClick={handleCollapseAll}
          style={{ fontFamily: "var(--homepage-sans-stack)" }}
          type="button"
        >
          Collapse all
        </button>
      )}
    </div>
  );
}

// ─── Fixed-height content area prevents layout jumping ───────

const CONTENT_HEIGHT = 520;

// ─── Brief card ──────────────────────────────────────────────

function BriefDetailCard({
  brief,
  onClick,
}: {
  brief: ExploreBriefCard;
  onClick: () => void;
}) {
  return (
    <button
      className="flex w-full flex-col gap-1.5 border-[var(--explore-border-light)] border-b px-5 py-4 text-left transition-colors last:border-b-0 hover:bg-[var(--explore-bg-highlight)]"
      onClick={onClick}
      style={{ fontFamily: "var(--homepage-sans-stack)" }}
      type="button"
    >
      <div className="flex items-center gap-2">
        <span className="text-[0.65rem] text-[var(--explore-text-muted)] tabular-nums">
          {brief.timeAgo}
        </span>
      </div>
      <p className="font-semibold text-[0.92rem] text-[var(--explore-text-primary)] leading-[1.4]">
        {brief.title}
      </p>
      {brief.tldr && (
        <p className="line-clamp-2 text-[0.82rem] text-[var(--explore-text-secondary)] leading-[1.5]">
          {brief.tldr}
        </p>
      )}
    </button>
  );
}

// ─── Sidebar navigation ──────────────────────────────────────

function CategorySidebar({
  groups,
  activeSlug,
  onSelect,
}: {
  groups: BriefGroup[];
  activeSlug: string;
  onSelect: (slug: string) => void;
}) {
  return (
    <nav className="flex flex-col gap-0.5">
      {groups.map((group) => {
        const isActive = group.categorySlug === activeSlug;

        return (
          <button
            className={`relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-left transition-colors ${
              isActive
                ? "bg-[var(--explore-bg-card)] font-semibold text-[var(--explore-text-primary)] shadow-[var(--explore-inline-shadow)]"
                : "text-[var(--explore-text-secondary)] hover:bg-[var(--explore-bg-highlight)] hover:text-[var(--explore-text-primary)]"
            }`}
            key={group.categorySlug}
            onClick={() => onSelect(group.categorySlug)}
            style={{ fontFamily: "var(--homepage-sans-stack)" }}
            type="button"
          >
            {/* Yellow active indicator strip */}
            {isActive && (
              <span className="absolute top-1.5 bottom-1.5 left-0 w-[3px] rounded-full bg-[var(--explore-accent-yellow)]" />
            )}
            <span className="text-[0.85rem]">{group.categoryEmoji}</span>
            <span className="min-w-0 flex-1 truncate text-[0.82rem]">
              {group.categoryDisplayName}
            </span>
            <span
              className={`ml-auto shrink-0 rounded-full px-1.5 py-0.5 font-semibold text-[0.65rem] tabular-nums ${
                isActive
                  ? "bg-[var(--explore-accent-yellow)] text-[var(--explore-on-accent-text)]"
                  : "text-[var(--explore-text-muted)]"
              }`}
            >
              {group.briefs.length}
            </span>
          </button>
        );
      })}
    </nav>
  );
}

// ─── Export ──────────────────────────────────────────────────

interface BriefsSidebarSectionProps {
  briefs: ExploreBriefCard[];
  briefsReportDate: string;
  className?: string;
}

export function BriefsSidebarSection({
  briefs,
  briefsReportDate: _briefsReportDate,
  className,
}: BriefsSidebarSectionProps) {
  const groups = useMemo(() => groupBriefsByCategory({ briefs }), [briefs]);

  const [activeCategory, setActiveCategory] = useState(
    groups[0]?.categorySlug ?? ""
  );
  const [selectedBrief, setSelectedBrief] = useState<ExploreBriefCard | null>(
    null
  );
  const [dialogOpen, setDialogOpen] = useState(false);
  const [openMethod, setOpenMethod] = useState<BriefOpenMethod>("click");
  const [storiesOpen, setStoriesOpen] = useState(false);
  const [allViewed, setAllViewed] = useState(false);
  const pendingAllViewed = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const overscrollAccum = useRef(0);

  const handleBriefClick = useCallback((brief: ExploreBriefCard) => {
    setOpenMethod("click");
    setSelectedBrief(brief);
    setDialogOpen(true);
  }, []);

  useBriefDeepLink({
    briefs,
    onOpen: useCallback((brief: ExploreBriefCard) => {
      setOpenMethod("deep_link");
      setSelectedBrief(brief);
      setDialogOpen(true);
    }, []),
  });

  const handleDialogOpenChange = useCallback((open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      clearBriefDeepLinkParams();
    }
  }, []);
  const categoryBriefs = useMemo(() => {
    if (!selectedBrief) {
      return;
    }
    const group = groups.find(
      (candidate) =>
        candidate.categorySlug ===
        (selectedBrief.categorySlug ?? "uncategorized")
    );
    return group?.briefs;
  }, [groups, selectedBrief]);
  const handleNavigateBrief = useCallback(
    (brief: ExploreBriefCard) => {
      handleBriefClick(brief);
    },
    [handleBriefClick]
  );

  const activeGroup = groups.find((g) => g.categorySlug === activeCategory);
  const activeBriefs = activeGroup?.briefs ?? [];
  const activeIndex = groups.findIndex(
    (g) => g.categorySlug === activeCategory
  );

  // Switch category on overscroll at edges
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const THRESHOLD = 300;

    const handleWheel = (e: WheelEvent) => {
      const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 2;
      const atTop = el.scrollTop < 2;

      if (atBottom && e.deltaY > 0 && activeIndex < groups.length - 1) {
        overscrollAccum.current += e.deltaY;
        if (overscrollAccum.current > THRESHOLD) {
          overscrollAccum.current = 0;
          setActiveCategory(groups[activeIndex + 1].categorySlug);
        }
        e.preventDefault();
      } else if (atTop && e.deltaY < 0 && activeIndex > 0) {
        overscrollAccum.current += Math.abs(e.deltaY);
        if (overscrollAccum.current > THRESHOLD) {
          overscrollAccum.current = 0;
          setActiveCategory(groups[activeIndex - 1].categorySlug);
        }
        e.preventDefault();
      } else {
        overscrollAccum.current = 0;
      }
    };

    el.addEventListener("wheel", handleWheel, { passive: false });
    return () => el.removeEventListener("wheel", handleWheel);
  }, [activeIndex, groups]);

  // Reset scroll position when switching category
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeCategory is the trigger
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    overscrollAccum.current = 0;
  }, [activeCategory]);

  if (briefs.length === 0) {
    return null;
  }

  return (
    <section className={className}>
      {/* Header — play ring + title */}
      <div className="mb-3 px-4 lg:px-0">
        <PlayAllButton
          allViewed={allViewed}
          onPlay={() => setStoriesOpen(true)}
          totalBriefs={briefs.length}
        />
      </div>

      {/* Desktop: sidebar + fixed-height scrollable content */}
      <div
        className="hidden overflow-hidden rounded-2xl lg:block"
        style={{
          backgroundColor: "var(--explore-bg-card)",
          boxShadow: "var(--explore-inline-shadow)",
        }}
      >
        <div className="grid grid-cols-[1fr_210px]">
          {/* Content — fixed height with inner scroll + category auto-switch */}
          <div
            className="overflow-y-auto"
            ref={scrollRef}
            style={{ height: `${CONTENT_HEIGHT}px` }}
          >
            {activeBriefs.length > 0 ? (
              activeBriefs.map((brief) => (
                <BriefDetailCard
                  brief={brief}
                  key={brief.briefId}
                  onClick={() => handleBriefClick(brief)}
                />
              ))
            ) : (
              <p className="py-8 text-center text-[0.82rem] text-[var(--explore-text-muted)]">
                No briefs in this category
              </p>
            )}
          </div>

          {/* Sidebar — right side */}
          <div
            className="p-3"
            style={{
              backgroundColor: "var(--explore-bg-sidebar)",
              borderLeft: "1px solid var(--explore-border-light)",
            }}
          >
            <CategorySidebar
              activeSlug={activeCategory}
              groups={groups}
              onSelect={setActiveCategory}
            />
          </div>
        </div>
      </div>

      {/* Mobile: compact A-style with categories, dots, accordion */}
      <MobileBriefsList groups={groups} onBriefClick={handleBriefClick} />

      <BriefPreviewDialog
        brief={selectedBrief}
        categoryBriefs={categoryBriefs}
        onNavigateBrief={handleNavigateBrief}
        onOpenChange={handleDialogOpenChange}
        open={dialogOpen}
        openMethod={openMethod}
      />

      {storiesOpen && (
        <BriefStoriesVerticalOverlay
          briefs={briefs}
          onAllViewed={() => {
            pendingAllViewed.current = true;
          }}
          onClose={() => {
            setStoriesOpen(false);
            if (pendingAllViewed.current) {
              // Small delay so the overlay unmounts first, then animation plays visibly
              setTimeout(() => setAllViewed(true), 100);
            }
          }}
        />
      )}
    </section>
  );
}
